import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  type User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { useLanguage } from '../contexts/LanguageContext';
import type { SessionScope } from '@mbbe/config';
import { getFirebaseAuth } from '../lib/firebase';
import { customerSession } from '../lib/sessions';
import { getCustomerProfile } from './lib/customerApi';
import { useOptionalPortalLanguagePreference } from './PortalLanguagePreference';
import { isActivePortalProfile, type PortalCustomerProfile } from './lib/portalAccess';

export interface CustomerAuthValue {
  user: User | null;
  me: PortalCustomerProfile | null;
  isLoading: boolean;
  isCustomer: boolean;
  profileError: boolean;
  accessDenied: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  retryProfile: () => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthValue | null>(null);
const isCustomerAccessDenied = (error: unknown) =>
  error instanceof Error && [
    'Insufficient permissions',
    'CUSTOMER_ACCESS_DENIED',
    'CUSTOMER_ACCESS_DISABLED',
    'Customer access disabled',
  ].includes(error.message);

export const SESSION_SUPERSEDED = 'SESSION_SUPERSEDED';

const profileSignature = (profile: PortalCustomerProfile) =>
  JSON.stringify([
    profile.role ?? null,
    profile.member?.role ?? null,
    profile.member?.status ?? null,
    profile.organization?.id ?? null,
    profile.access?.allProjects ?? null,
    [...(profile.access?.capabilities ?? [])].sort(),
    [...(profile.access?.projectIds ?? [])].sort(),
  ]);

export function CustomerAuthProvider({
  children,
  beforeSignOut,
}: {
  children: React.ReactNode;
  beforeSignOut?: () => Promise<unknown>;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [me, setMe] = useState<PortalCustomerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCustomer, setIsCustomer] = useState(false);
  const [profileError, setProfileError] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  // Identity binding (replaces a boolean "sign-in handled" flag): the scope a
  // profile request was started for, and the last access state committed.
  const loadStartedRef = useRef<SessionScope | null>(null);
  const committedRef = useRef<{ uid: string | null; signature: string } | null>(null);
  const { setLanguage } = useLanguage();
  const languagePreference = useOptionalPortalLanguagePreference();

  const clearAccountState = () => {
    committedRef.current = null;
    setMe(null);
    setIsCustomer(false);
    setProfileError(false);
    setAccessDenied(false);
  };

  // Changed role/capabilities/project grants for the same account rotate the
  // scope so data cached under the previous access level is dropped.
  const settleScope = (scope: SessionScope, profile: PortalCustomerProfile): SessionScope => {
    const signature = profileSignature(profile);
    const previous = committedRef.current;
    committedRef.current = { uid: scope.uid, signature };
    if (!previous || previous.uid !== scope.uid || previous.signature === signature) return scope;
    const rotated = customerSession.begin(scope.uid);
    loadStartedRef.current = rotated;
    return rotated;
  };

  /**
   * Loads the profile for `scope`. A response that arrives after the session
   * changed (sign-out, another account, unmount) is discarded by identity:
   * it cannot restore the previous account's profile, role or language.
   * Returns the scope that owns the state afterwards, or null when superseded.
   */
  const loadProfile = async (scope: SessionScope): Promise<SessionScope | null> => {
    loadStartedRef.current = scope;
    const adoptLanguage = languagePreference?.beginProfileLoad(scope.uid ?? '');
    setProfileError(false);
    setAccessDenied(false);
    try {
      const profile = await getCustomerProfile();
      if (!scope.isCurrent()) return null;
      const owner = settleScope(scope, profile);
      setMe(profile);
      const allowed = isActivePortalProfile(profile);
      setIsCustomer(allowed);
      setAccessDenied(!allowed);
      if (profile.language) {
        if (adoptLanguage) adoptLanguage(profile.language);
        else setLanguage(profile.language);
      }
      return owner;
    } catch (error) {
      if (!scope.isCurrent()) return null;
      setMe(null);
      setIsCustomer(false);
      if (isCustomerAccessDenied(error)) {
        setAccessDenied(true);
        return scope;
      }
      setProfileError(true);
      return scope;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getFirebaseAuth(), async (nextUser) => {
      const uid = nextUser?.uid ?? null;
      // The sign-in flow (or an earlier event) already owns this identity.
      if (uid !== null && customerSession.current.uid === uid && loadStartedRef.current === customerSession.current) return;

      const scope = customerSession.ensure(uid);
      setIsLoading(true);
      languagePreference?.setAccount(nextUser?.uid ?? null);
      setUser(nextUser);
      clearAccountState();
      if (!nextUser) {
        setIsLoading(false);
        return;
      }
      const owner = await loadProfile(scope);
      if (owner?.isCurrent()) setIsLoading(false);
    });
    return () => {
      unsubscribe();
      customerSession.end();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const auth = getFirebaseAuth();
    let credential;
    try {
      credential = await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      throw new Error(`FIREBASE_AUTH_ERROR:${(error as { code?: string })?.code || 'unknown'}`);
    }
    // Starting B's scope aborts A's requests and drops A's cached data
    // synchronously, before B's state is rendered.
    const scope = customerSession.begin(credential.user.uid);
    loadStartedRef.current = scope;
    clearAccountState();
    languagePreference?.setAccount(credential.user.uid);
    setUser(credential.user);
    setIsLoading(true);
    const adoptLanguage = languagePreference?.beginProfileLoad(credential.user.uid);

    const abandon = async (flags: { denied: boolean }) => {
      await firebaseSignOut(auth);
      // Another account may have signed in while the sign-out was in flight.
      if (customerSession.current.uid !== null && !scope.isCurrent()) return;
      customerSession.ensure(null);
      languagePreference?.setAccount(null);
      setUser(null);
      clearAccountState();
      setAccessDenied(flags.denied);
      setProfileError(!flags.denied);
    };

    try {
      const profile = await getCustomerProfile();
      if (!scope.isCurrent()) throw new Error(SESSION_SUPERSEDED);
      if (!isActivePortalProfile(profile)) {
        await abandon({ denied: true });
        throw new Error('NOT_CUSTOMER');
      }
      const owner = settleScope(scope, profile);
      loadStartedRef.current = owner;
      setMe(profile);
      setIsCustomer(true);
      setProfileError(false);
      setAccessDenied(false);
      if (profile.language) {
        if (adoptLanguage) adoptLanguage(profile.language);
        else setLanguage(profile.language);
      }
    } catch (error) {
      if (error instanceof Error && (error.message === 'NOT_CUSTOMER' || error.message === SESSION_SUPERSEDED)) throw error;
      // A request cancelled by a newer session must not sign the newer account out.
      if (!scope.isCurrent()) throw new Error(SESSION_SUPERSEDED);
      const denied = isCustomerAccessDenied(error);
      await abandon({ denied });
      throw new Error(denied ? 'NOT_CUSTOMER' : 'CUSTOMER_CHECK_FAILED');
    } finally {
      if (customerSession.current.uid === credential.user.uid || customerSession.current.uid === null) setIsLoading(false);
    }
  };

  const signOut = async () => {
    if (beforeSignOut) await beforeSignOut().catch(() => undefined);
    const previous = customerSession.current;
    // Drop the account's requests and cached data before the network sign-out.
    customerSession.end();
    try {
      await firebaseSignOut(getFirebaseAuth());
    } catch (error) {
      if (customerSession.current.uid === null && previous.uid) customerSession.begin(previous.uid);
      throw error;
    }
    languagePreference?.setAccount(null);
    setUser(null);
    clearAccountState();
  };

  const retryProfile = async () => {
    if (!user) return;
    const scope = customerSession.ensure(user.uid);
    setIsLoading(true);
    const owner = await loadProfile(scope);
    if (owner?.isCurrent()) setIsLoading(false);
  };

  return (
    <CustomerAuthContext.Provider value={{ user, me, isLoading, isCustomer, profileError, accessDenied, signIn, signOut, retryProfile }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth(): CustomerAuthValue {
  const context = useContext(CustomerAuthContext);
  if (!context) throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  return context;
}
